import { Injectable } from "@angular/core";
import { animationFrameScheduler, BehaviorSubject, Observable, Subject } from "rxjs";
import { auditTime } from "rxjs/operators";

export type LogLevel = "log" | "info" | "warn" | "error" | "debug";

export interface LogEntry {
  id: number;
  timestamp: number;
  level: LogLevel;
  message: string;
}

/**
 * In-app log capture for dev/QA. Patches `console.*` to mirror every log into
 * a bounded ring buffer that the in-app Log Viewer can read. Also captures
 * `window.error` and unhandled promise rejections.
 *
 * NOTE : ne capture QUE les logs JS / WebView. Les NSLog iOS / Logcat Android
 * (plugins natifs Swift/Kotlin) restent invisibles — ils nécessitent
 * Xcode/adb ou un plugin natif custom.
 */
@Injectable({ providedIn: "root" })
export class LogService {
  private static readonly MAX_ENTRIES = 1000;
  /** Hard cap per-entry message length to bound memory on low-end devices. */
  private static readonly MAX_MESSAGE_BYTES = 8_192;
  /** Dedupe window: drop a logically-identical entry seen within this delay. */
  private static readonly DEDUPE_WINDOW_MS = 200;

  private entries: LogEntry[] = [];
  private nextId = 1;
  /** Pulsed subject — fires on every append, then throttled below. */
  private pulse$ = new Subject<void>();
  private snapshot$ = new BehaviorSubject<LogEntry[]>([]);
  private initialized = false;
  /** Last seen "<level>:<msg-prefix>" → timestamp, for short-window dedupe. */
  private lastSeen = new Map<string, number>();

  /**
   * Public stream of the current buffer. Throttled to one emission per
   * animation frame (≤60Hz) regardless of how many console.* calls fire
   * underneath. Without this, a 100 logs/sec burst would trigger 100
   * Angular CD cycles in subscribers (LogViewer, LogReporter).
   */
  readonly entries$: Observable<LogEntry[]>;

  constructor() {
    this.entries$ = this.snapshot$.asObservable();
    // Re-emit current snapshot whenever the pulse fires, throttled to rAF.
    this.pulse$.pipe(auditTime(0, animationFrameScheduler)).subscribe(() => {
      this.snapshot$.next(this.entries.slice());
    });
  }

  /** Idempotent. Appelé une seule fois au boot par AppComponent. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.patchConsole();
    this.attachGlobalHandlers();
  }

  getEntries(): LogEntry[] {
    return this.entries.slice();
  }

  clear(): void {
    this.entries = [];
    this.lastSeen.clear();
    this.snapshot$.next([]);
  }

  /** Returns the full log as a multi-line string ready for clipboard / share. */
  exportAsText(): string {
    return this.entries
      .map((e) => {
        const ts = new Date(e.timestamp).toISOString();
        return `[${ts}] [${e.level.toUpperCase()}] ${e.message}`;
      })
      .join("\n");
  }

  /** Adds a manual entry from app code (e.g. an HttpInterceptor). */
  push(level: LogLevel, message: string): void {
    this.append(level, message);
  }

  private append(level: LogLevel, message: string): void {
    // Trim oversized payloads — Angular instance dumps and large response
    // bodies can be 100KB+ each and would explode memory at 1000 entries.
    let msg = message;
    if (msg.length > LogService.MAX_MESSAGE_BYTES) {
      msg = msg.slice(0, LogService.MAX_MESSAGE_BYTES) + "…[truncated]";
    }
    // Short-window dedupe: window.error + Angular's default ErrorHandler
    // often both surface the SAME error as console.error → store once.
    const now = Date.now();
    const key = `${level}:${msg.slice(0, 200)}`;
    const last = this.lastSeen.get(key);
    if (last !== undefined && now - last < LogService.DEDUPE_WINDOW_MS) {
      this.lastSeen.set(key, now);
      return;
    }
    this.lastSeen.set(key, now);

    const entry: LogEntry = { id: this.nextId++, timestamp: now, level, message: msg };
    this.entries.push(entry);
    if (this.entries.length > LogService.MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - LogService.MAX_ENTRIES);
    }
    this.pulse$.next();
  }

  private patchConsole(): void {
    // Note: console.group / console.groupEnd / console.table / console.trace
    // are INTENTIONALLY NOT patched — they are formatting wrappers; their
    // arguments still flow through console.log/info/error via the bound
    // arguments below. console.assert is also not patched (rare in app code).
    const levels: LogLevel[] = ["log", "info", "warn", "error", "debug"];
    for (const level of levels) {
      const original = (console as any)[level]?.bind(console);
      (console as any)[level] = (...args: any[]) => {
        try {
          this.append(level, this.formatArgs(args));
        } catch (_) {
          // Never let a logging error crash the app — fall through to native.
        }
        original?.(...args);
      };
    }
  }

  private attachGlobalHandlers(): void {
    if (typeof window === "undefined") return;
    window.addEventListener("error", (e: ErrorEvent) => {
      const stack = e.error?.stack ? `\n${e.error.stack}` : "";
      this.append("error", `[window.error] ${e.message}${stack}`);
    });
    window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
      const reason = (e.reason && (e.reason.stack || e.reason.message)) || String(e.reason);
      this.append("error", `[unhandledrejection] ${reason}`);
    });
  }

  private formatArgs(args: any[]): string {
    return args
      .map((a) => {
        if (a === undefined) return "undefined";
        if (a === null) return "null";
        if (typeof a === "string") return a;
        if (a instanceof Error) {
          return a.stack ? `${a.message}\n${a.stack}` : a.message;
        }
        try {
          const s = JSON.stringify(a, this.replacer(), 2);
          return s && s.length > LogService.MAX_MESSAGE_BYTES
            ? s.slice(0, LogService.MAX_MESSAGE_BYTES) + "…[truncated]"
            : s ?? String(a);
        } catch (_) {
          return String(a);
        }
      })
      .join(" ");
  }

  /** Replacer that drops circular refs to avoid JSON.stringify throwing. */
  private replacer() {
    const seen = new WeakSet<object>();
    return (_key: string, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) return "[Circular]";
        seen.add(value);
      }
      return value;
    };
  }
}
